import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  input,
  InputSignal,
  Output,
  signal,
  Signal,
  viewChild,
  ViewChild,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import {Product, ProductSpec, Variation} from '@sportngin/se-commerce-sdk/models/clare';
import {SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeModalButton, SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {SeFePositionService} from 'se-fe-position';
import {SeFeSwitchComponent, SeFeSwitchModule} from 'se-fe-switch';
import {SeFeTableDataSource, SeFeTableModule, SeFeTableSortDirective} from 'se-fe-table';
import {InventoryVariation} from '../../../../../models/clare';

export interface VariantRow {
  status: boolean;
  name: string;
  id: string;
  synced: boolean;
  sku: string | undefined;
  available: string;
}

export const mapVariantToVariantRow = (value: InventoryVariation): VariantRow => {
  const row: VariantRow = {
    status: !!value.active,
    name: value.name ?? '',
    id: String(value.id),
    synced: true,
    sku: value.sku,
    available: value.inventory === null || value.inventory === undefined ? 'Unlimited' : String(value.inventory),
  };

  return row;
};

@Component({
  selector: 'product-detail-variants',
  imports: [
    CommonModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeGridModule,
    SeFeSwitchModule,
    SeFeTableModule,
    SeFeModalModule,
  ],
  templateUrl: './product-detail-variants.component.html',
  styleUrls: ['./product-detail-variants.component.scss'],
  providers: [SeFePositionService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailVariantsComponent {
  @Output() updateVariantStatus = new EventEmitter<{id: string; active: boolean}>();

  variants = input<Variation[]>([]);
  specs = input<ProductSpec[]>();
  product: InputSignal<Product | undefined> = input<Product>();
  readonly showInventory = input(true);

  confirmDisableVariantModal = viewChild.required<SeFeModalComponent>('confirmDisableVariants');
  switches = viewChildren(SeFeSwitchComponent);

  specOptionValues: Signal<{[key: string]: string[]} | undefined> = computed(() => {
    return this.reduceSpecsToMap(this.specs());
  });
  variantTableDatasource: Signal<SeFeTableDataSource> = computed(() => {
    const data = this.variants().map(mapVariantToVariantRow);

    return new SeFeTableDataSource(data, this.sortDirective());
  });
  sortDirective: WritableSignal<SeFeTableSortDirective | undefined> = signal(undefined);

  readonly variantColumns = computed(() => ['name', 'sku', ...(this.showInventory() ? ['available'] : []), 'status']);
  variantTableReady = false;

  disableVariantModalButtons: SeFeModalButton[] = [
    {
      cancel: true,
      label: 'Cancel',
      action: () => {
        this.confirmDisableVariantModal().close();
        this.variantEvent.source.reset();
        this.cdr.detectChanges();
      },
    },
    {
      label: 'Disable Variants',
      primary: true,
      action: () => {
        this.updateVariantStatus.emit(this.variantEvent);
        this.confirmDisableVariantModal().close();
      },
    },
  ];

  private variantEvent: {id: string; active: boolean; source: SeFeSwitchComponent};

  constructor(
    private cdr: ChangeDetectorRef,
    private ref: ElementRef
  ) {
    effect(() => {
      if (this.variantTableDatasource()) {
        this.variantTableReady = true;
        this.cdr.detectChanges();
      }
    });
  }

  @ViewChild(SeFeTableSortDirective)
  set sorter(sorter: SeFeTableSortDirective) {
    if (!sorter) return;
    this.sortDirective.set(sorter);
  }

  async action(event: {id: string; active: boolean}): Promise<void> {
    const allOtherVariantsDisabled = this.variants()
      .filter(v => String(v.id) !== event.id)
      .every(v => !v.active);

    if (allOtherVariantsDisabled && !event.active && !!this.product()?.active) {
      const focus: Element = this.ref.nativeElement.querySelector(`[id="${event.id}"]`)!;
      const source = this.switches().find(s => s.id === event.id)!;
      this.variantEvent = {...event, source};
      this.confirmDisableVariantModal().open(focus);
    } else {
      this.updateVariantStatus.emit(event);
    }
  }

  reduceSpecsToMap(specs?: ProductSpec[]): {[key: string]: string[]} | undefined {
    return specs?.reduce((acc: {[name: string]: string[]}, spec: ProductSpec) => {
      acc[spec.name!] = spec.spec_options?.map(option => option.name!) || [];
      return acc;
    }, {});
  }
}
